import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ChevronRight, ChevronLeft, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

export default function QuestionnairePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/questionnaire/${token}`)
      .then(res => {
        if (res.data.already_submitted) {
          setDone(true);
        }
        setData(res.data);
      })
      .catch(() => setError("Lien invalide ou questionnaire introuvable."))
      .finally(() => setLoading(false));
  }, [token]);

  const questions = data?.questions || [];
  const current = questions[step];
  const total = questions.length;
  const progress = total > 0 ? ((step + 1) / total) * 100 : 0;

  const setValue = (key, value) => setResponses(prev => ({ ...prev, [key]: value }));

  const toggleMulti = (key, option) => {
    setResponses(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(option) ? arr.filter(x => x !== option) : [...arr, option] };
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/questionnaire/${token}/submit`, { responses });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="h-16 w-16 rounded-2xl bg-[#EF4444]/10 flex items-center justify-center mx-auto mb-4">
            <span className="text-[#EF4444] text-2xl font-bold">!</span>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Lien invalide</h2>
          <p className="text-[#94A3B8]">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="questionnaire-success">
          <div className="h-16 w-16 rounded-2xl bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-[#10B981]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Merci {data?.prospect_name} !</h2>
          <p className="text-[#94A3B8] leading-relaxed">
            Vos reponses ont ete enregistrees avec succes. Notre equipe va analyser votre projet
            et vous recontactera rapidement avec une proposition personnalisee.
          </p>
          <Link to="/" className="inline-block mt-6">
            <Button className="bg-[#0099CC] hover:bg-[#0088B5] text-white">Retour au site</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="questionnaire-page">
      {/* Header */}
      <div className="border-b border-[#2A3441] bg-[#0B0F19]/90 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#0099CC] to-[#0077AA] flex items-center justify-center font-bold text-white text-sm">G</div>
            <span className="font-bold tracking-wide text-lg text-white">GLOTECH<span className="text-[#0099CC]"> OS</span></span>
          </Link>
          <span className="text-[#64748b] text-sm">Questionnaire projet</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-xs text-[#64748b] mb-2">
            <span>Question {step + 1} sur {total}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-1.5 bg-[#1E293B] rounded-full overflow-hidden">
            <div className="h-full bg-[#0099CC] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Greeting on first step */}
        {step === 0 && (
          <p className="text-[#94A3B8] text-sm mb-6">
            Bonjour <span className="text-white font-medium">{data?.prospect_name}</span>, aidez-nous a mieux comprendre votre projet.
          </p>
        )}

        {/* Question */}
        {current && (
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-8 animate-fade-in" data-testid={`question-${current.key}`}>
            <h2 className="text-xl font-semibold text-white mb-6">{current.question}</h2>

            {current.type === "select" && (
              <div className="grid gap-2">
                {current.options.map(opt => (
                  <button key={opt} onClick={() => setValue(current.key, opt)}
                    className={`text-left px-4 py-3.5 rounded-xl border transition-all text-sm ${
                      responses[current.key] === opt
                        ? "border-[#0099CC] bg-[#0099CC]/10 text-white"
                        : "border-[#2A3441] text-[#94A3B8] hover:border-[#0099CC]/40 hover:text-white"
                    }`}
                    data-testid={`option-${current.key}-${opt.slice(0, 20).replace(/\s/g, '-')}`}>
                    {opt}
                  </button>
                ))}
              </div>
            )}

            {current.type === "multiselect" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {current.options.map(opt => {
                  const selected = (responses[current.key] || []).includes(opt);
                  return (
                    <button key={opt} onClick={() => toggleMulti(current.key, opt)}
                      className={`text-left px-4 py-3 rounded-xl border transition-all text-sm ${
                        selected
                          ? "border-[#0099CC] bg-[#0099CC]/10 text-white"
                          : "border-[#2A3441] text-[#94A3B8] hover:border-[#0099CC]/40 hover:text-white"
                      }`}>
                      <span className={`inline-block w-4 h-4 rounded border mr-2 align-middle ${selected ? "bg-[#0099CC] border-[#0099CC]" : "border-[#64748b]"}`}>
                        {selected && <CheckCircle2 className="h-4 w-4 text-white" />}
                      </span>
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}

            {current.type === "text" && (
              <input type="text" value={responses[current.key] || ""}
                onChange={e => setValue(current.key, e.target.value)}
                placeholder={current.placeholder || ""}
                className="w-full bg-[#0B0F19] border border-[#2A3441] rounded-xl px-4 py-3.5 text-white placeholder:text-[#64748b] focus:border-[#0099CC] focus:outline-none"
                data-testid={`input-${current.key}`} />
            )}

            {current.type === "textarea" && (
              <textarea value={responses[current.key] || ""}
                onChange={e => setValue(current.key, e.target.value)}
                placeholder={current.placeholder || ""}
                rows={5}
                className="w-full bg-[#0B0F19] border border-[#2A3441] rounded-xl px-4 py-3.5 text-white placeholder:text-[#64748b] focus:border-[#0099CC] focus:outline-none resize-none"
                data-testid={`textarea-${current.key}`} />
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 0}
            className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid="prev-btn">
            <ChevronLeft className="h-4 w-4 mr-1" />Precedent
          </Button>

          {step < total - 1 ? (
            <Button onClick={() => setStep(s => s + 1)}
              className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="next-btn">
              Suivant<ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting}
              className="bg-[#10B981] hover:bg-[#0d9668] text-white" data-testid="submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Envoyer mes reponses
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
